import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import drAmyMark from "@/assets/dr-amy.svg";
import orthodonticsMark from "@/assets/orthodontics.svg";
import { LOCATIONS } from "@/data/locations";

const NAV_LINKS: { to: string; label: string }[] = [
  { to: "/#services", label: "Services" },
  { to: "/#patient-info", label: "Patient Info" },
  { to: "/#contact", label: "Contact" },
];

const DOCTORS: { to: string; name: string; credentials: string }[] = [
  { to: "/dr-amy", name: "Dr. Amy Harel", credentials: "DDS, MS" },
  { to: "/dr-bilous", name: "Dr. Oresta Bilous", credentials: "DMD" },
];

function NavDropdown({
  label,
  triggerTo,
  children,
}: {
  label: string;
  triggerTo: string;
  children: React.ReactNode;
}) {
  const [hovered, setHovered] = useState(false);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const open = () => {
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    setHovered(true);
  };
  const close = () => {
    leaveTimer.current = setTimeout(() => setHovered(false), 120);
  };

  return (
    <div
      className="relative"
      onMouseEnter={open}
      onMouseLeave={close}
      onFocusCapture={open}
      onBlurCapture={close}
    >
      <Link
        to={triggerTo}
        className="flex items-center gap-1 text-sm font-medium text-sage-800 transition hover:text-blush-500"
        aria-haspopup="true"
        aria-expanded={hovered}
      >
        {label}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          className={`h-3.5 w-3.5 transition-transform duration-150 ${hovered ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </Link>

      {hovered && (
        <div className="absolute left-1/2 top-full -translate-x-1/2 pt-3">
          <div className="w-56 overflow-hidden rounded-xl border border-sage-100 bg-cream-50 shadow-lg ring-1 ring-black/5">
            <div className="p-1.5">{children}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function DoctorsDropdown() {
  return (
    <NavDropdown label="Meet The Doctors" triggerTo="/#about">
      {DOCTORS.map((doctor) => (
        <Link
          key={doctor.to}
          to={doctor.to}
          className="block rounded-lg px-3 py-2.5 text-sm font-medium text-sage-800 transition hover:bg-sage-50 hover:text-blush-500"
        >
          <span className="block">{doctor.name}</span>
          <span className="block text-xs font-normal text-sage-500">
            {doctor.credentials}
          </span>
        </Link>
      ))}
    </NavDropdown>
  );
}

function LocationsDropdown() {
  return (
    <NavDropdown label="Locations" triggerTo="/#locations">
      {LOCATIONS.map((loc) => (
        <Link
          key={loc.slug}
          to={`/locations/${loc.slug}`}
          className="block rounded-lg px-3 py-2.5 text-sm font-medium text-sage-800 transition hover:bg-sage-50 hover:text-blush-500"
        >
          <span className="block">{loc.shortName}</span>
          <span className="block text-xs font-normal text-sage-500">
            {loc.address[1]}
          </span>
        </Link>
      ))}
    </NavDropdown>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileDoctorsOpen, setMobileDoctorsOpen] = useState(false);
  const [mobileLocationsOpen, setMobileLocationsOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-40 transition-all ${
        scrolled
          ? "bg-cream-50/95 shadow-soft backdrop-blur"
          : "bg-cream-50/70 backdrop-blur-sm"
      }`}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4 lg:px-8">
        <Link
          to="/"
          className="flex items-center gap-3"
          aria-label="Dr. Amy Orthodontics"
        >
          <img src={drAmyMark} alt="" className="h-9 w-auto sm:h-11" />
          <img
            src={orthodonticsMark}
            alt=""
            className="h-[7px] w-auto sm:h-[9px]"
          />
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          <DoctorsDropdown />
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="text-sm font-medium text-sage-800 transition hover:text-blush-500"
            >
              {link.label}
            </Link>
          ))}
          <LocationsDropdown />
          <Link to="/#contact" className="btn-primary py-2 text-xs">
            Request Appointment
          </Link>
        </nav>

        <button
          type="button"
          aria-label="Open menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-sage-200 text-sage-700 md:hidden"
        >
          <span className="sr-only">Toggle navigation</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
          >
            {open ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 6l12 12M18 6L6 18"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 7h16M4 12h16M4 17h16"
              />
            )}
          </svg>
        </button>
      </div>

      {open && (
        <div className="border-t border-sage-100 bg-cream-50 md:hidden">
          <nav className="mx-auto flex max-w-6xl flex-col gap-1 px-6 py-4">
            <button
              type="button"
              onClick={() => setMobileDoctorsOpen((v) => !v)}
              className="flex w-full items-center justify-between rounded-lg px-3 py-3 text-sm font-medium text-sage-800 hover:bg-sage-50"
            >
              Meet The Doctors
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className={`h-4 w-4 transition-transform duration-150 ${mobileDoctorsOpen ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {mobileDoctorsOpen && (
              <div className="ml-3 flex flex-col gap-1 border-l-2 border-sage-100 pl-3">
                {DOCTORS.map((doctor) => (
                  <Link
                    key={doctor.to}
                    to={doctor.to}
                    onClick={() => setOpen(false)}
                    className="rounded-lg px-3 py-2.5 text-sm font-medium text-sage-800 hover:bg-sage-50"
                  >
                    {doctor.name}
                    <span className="ml-2 text-xs font-normal text-sage-500">
                      {doctor.credentials}
                    </span>
                  </Link>
                ))}
              </div>
            )}
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-3 text-sm font-medium text-sage-800 hover:bg-sage-50"
              >
                {link.label}
              </Link>
            ))}
            <button
              type="button"
              onClick={() => setMobileLocationsOpen((v) => !v)}
              className="flex w-full items-center justify-between rounded-lg px-3 py-3 text-sm font-medium text-sage-800 hover:bg-sage-50"
            >
              Locations
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className={`h-4 w-4 transition-transform duration-150 ${mobileLocationsOpen ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {mobileLocationsOpen && (
              <div className="ml-3 flex flex-col gap-1 border-l-2 border-sage-100 pl-3">
                {LOCATIONS.map((loc) => (
                  <Link
                    key={loc.slug}
                    to={`/locations/${loc.slug}`}
                    onClick={() => setOpen(false)}
                    className="rounded-lg px-3 py-2.5 text-sm font-medium text-sage-800 hover:bg-sage-50"
                  >
                    {loc.shortName}
                    <span className="ml-2 text-xs font-normal text-sage-500">
                      {loc.address[1]}
                    </span>
                  </Link>
                ))}
              </div>
            )}
            <Link
              to="/#contact"
              onClick={() => setOpen(false)}
              className="btn-primary mt-2"
            >
              Request Appointment
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
